import { TransportClientId } from '..';
import {
  SessionConnecting,
  SessionConnectingListeners,
} from './SessionConnecting';
import {
  SessionNoConnection,
  SessionNoConnectionListeners,
} from './SessionNoConnection';
import {
  IdentifiedSession,
  IdentifiedSessionProps,
  IdentifiedSessionWithGracePeriod,
  IdentifiedSessionWithGracePeriodProps,
  SessionOptions,
} from './common';
import {
  PropagationContext,
  createConnectionTelemetryInfo,
  createSessionTelemetryInfo,
} from '../../tracing';
import {
  SessionWaitingForHandshake,
  SessionWaitingForHandshakeListeners,
} from './SessionWaitingForHandshake';
import {
  SessionHandshaking,
  SessionHandshakingListeners,
} from './SessionHandshaking';
import {
  SessionConnected,
  SessionConnectedListeners,
} from './SessionConnected';
import { generateId } from '../id';
import { Connection } from '../connection';
import { Logger } from '../../logging';
import {
  SessionBackingOff,
  SessionBackingOffListeners,
} from './SessionBackingOff';
import { EncodedTransportMessage, ProtocolVersion } from '../message';
import { Tracer } from '@opentelemetry/api';
import { CodecMessageAdapter } from '../../codec';

/**
 * Only the server compiles its inbound validator: compiling generates code at
 * runtime, which a strict CSP blocks, and the client may be a browser.
 */
const serverValidation = { precompileValidator: true };

function inheritSharedSession(
  session: IdentifiedSession,
): Omit<IdentifiedSessionProps, 'listeners'> {
  return {
    id: session.id,
    from: session.from,
    to: session.to,
    seq: session.seq,
    ack: session.ack,
    seqSent: session.seqSent,
    sendBuffer: session.sendBuffer,
    sendBufferDrainWaiter: session.sendBufferDrainWaiter,
    telemetry: session.telemetry,
    options: session.options,
    log: session.log,
    tracer: session.tracer,
    protocolVersion: session.protocolVersion,
    codec: session.codec,
  };
}

function inheritSharedSessionWithGrace(
  session: IdentifiedSessionWithGracePeriod,
): Omit<IdentifiedSessionWithGracePeriodProps, 'listeners'> {
  return {
    ...inheritSharedSession(session),
    graceExpiryTime: session.graceExpiryTime,
  };
}

export const SessionStateGraph = {
  entrypoints: {
    NoConnection: (
      to: TransportClientId,
      from: TransportClientId,
      listeners: SessionNoConnectionListeners,
      options: SessionOptions,
      protocolVersion: ProtocolVersion,
      tracer: Tracer,
      log?: Logger,
    ) => {
      const id = `session-${generateId()}`;
      const telemetry = createSessionTelemetryInfo(tracer, id, to, from);
      const sendBuffer: Array<EncodedTransportMessage> = [];

      const session = new SessionNoConnection({
        listeners,
        id,
        from,
        to,
        seq: 0,
        ack: 0,
        seqSent: 0,
        graceExpiryTime: Date.now() + options.sessionDisconnectGraceMs,
        sendBuffer,
        sendBufferDrainWaiter: undefined,
        telemetry,
        options,
        protocolVersion,
        tracer,
        log,
        codec: new CodecMessageAdapter(options.codec),
      });

      session.log?.info(`session ${session.id} created in NoConnection state`, {
        ...session.loggingMetadata,
        tags: ['state-transition'],
      });

      return session;
    },
    WaitingForHandshake: <ConnType extends Connection>(
      from: TransportClientId,
      conn: ConnType,
      listeners: SessionWaitingForHandshakeListeners,
      options: SessionOptions,
      tracer: Tracer,
      log?: Logger,
    ): SessionWaitingForHandshake<ConnType> => {
      const session = new SessionWaitingForHandshake({
        conn,
        listeners,
        from,
        options,
        tracer,
        log,
        codec: new CodecMessageAdapter(options.codec, serverValidation),
      });

      session.log?.info(`session created in WaitingForHandshake state`, {
        ...session.loggingMetadata,
        tags: ['state-transition'],
      });

      return session;
    },
  },
  // All of the transitions 'move'/'consume' the old session and return a new one.
  // After a session is transitioned, any usage of the old session will throw.
  transition: {
    // happy path transitions
    NoConnectionToBackingOff: (
      oldSession: SessionNoConnection,
      backoffMs: number,
      listeners: SessionBackingOffListeners,
    ): SessionBackingOff => {
      const carriedState = inheritSharedSessionWithGrace(oldSession);
      oldSession._handleStateExit();

      const session = new SessionBackingOff({
        backoffMs,
        listeners,
        ...carriedState,
      });

      session.log?.info(
        `session ${session.id} transition from NoConnection to BackingOff`,
        {
          ...session.loggingMetadata,
          tags: ['state-transition'],
        },
      );

      return session;
    },
    BackingOffToConnecting: <ConnType extends Connection>(
      oldSession: SessionBackingOff,
      connPromise: Promise<ConnType>,
      listeners: SessionConnectingListeners,
    ): SessionConnecting<ConnType> => {
      const carriedState = inheritSharedSessionWithGrace(oldSession);
      oldSession._handleStateExit();

      const session = new SessionConnecting({
        connPromise,
        listeners,
        ...carriedState,
      });

      session.log?.info(
        `session ${session.id} transition from BackingOff to Connecting`,
        {
          ...session.loggingMetadata,
          tags: ['state-transition'],
        },
      );

      return session;
    },
    ConnectingToHandshaking: <ConnType extends Connection>(
      oldSession: SessionConnecting<ConnType>,
      conn: ConnType,
      listeners: SessionHandshakingListeners,
    ): SessionHandshaking<ConnType> => {
      const carriedState = inheritSharedSessionWithGrace(oldSession);
      oldSession._handleStateExit();

      const session = new SessionHandshaking({
        conn,
        listeners,
        ...carriedState,
      });

      conn.telemetry = createConnectionTelemetryInfo(
        session.tracer,
        conn,
        session.telemetry,
      );
      session.log?.info(
        `session ${session.id} transition from Connecting to Handshaking`,
        {
          ...session.loggingMetadata,
          tags: ['state-transition'],
        },
      );

      return session;
    },
    HandshakingToConnected: <ConnType extends Connection>(
      oldSession: SessionHandshaking<ConnType>,
      listeners: SessionConnectedListeners,
    ): SessionConnected<ConnType> => {
      const carriedState = inheritSharedSession(oldSession);
      const conn = oldSession.conn;
      oldSession._handleStateExit();

      const session = new SessionConnected({
        conn,
        listeners,
        ...carriedState,
      });

      session.startHeartbeatWatchdog();

      session.log?.info(
        `session ${session.id} transition from Handshaking to Connected`,
        {
          ...session.loggingMetadata,
          tags: ['state-transition'],
        },
      );

      return session;
    },
    WaitingForHandshakeToConnected: <ConnType extends Connection>(
      pendingSession: SessionWaitingForHandshake<ConnType>,
      oldSession: SessionNoConnection | undefined,
      sessionId: string,
      to: TransportClientId,
      propagationCtx: PropagationContext | undefined,
      listeners: SessionConnectedListeners,
      protocolVersion: ProtocolVersion,
    ): SessionConnected<ConnType> => {
      const conn = pendingSession.conn;
      const { from, options } = pendingSession;
      const carriedState: Omit<IdentifiedSessionProps, 'listeners'> = oldSession
        ? // old session exists, inherit state
          inheritSharedSession(oldSession)
        : // old session does not exist, create new state
          ({
            id: sessionId,
            from,
            to,
            seq: 0,
            ack: 0,
            seqSent: 0,
            sendBuffer: [],
            sendBufferDrainWaiter: undefined,
            telemetry: createSessionTelemetryInfo(
              pendingSession.tracer,
              sessionId,
              to,
              from,
              propagationCtx,
            ),
            options,
            tracer: pendingSession.tracer,
            log: pendingSession.log,
            protocolVersion,
            codec: new CodecMessageAdapter(options.codec, serverValidation),
          } satisfies Omit<IdentifiedSessionProps, 'listeners'>);

      pendingSession._handleStateExit();
      oldSession?._handleStateExit();

      const session = new SessionConnected({
        conn,
        listeners,
        ...carriedState,
      });

      session.startHeartbeatWatchdog();

      conn.telemetry = createConnectionTelemetryInfo(
        session.tracer,
        conn,
        session.telemetry,
      );
      session.log?.info(
        `session ${session.id} transition from WaitingForHandshake to Connected`,
        {
          ...session.loggingMetadata,
          tags: ['state-transition'],
        },
      );

      return session;
    },
    // disconnect paths
    BackingOffToNoConnection: (
      oldSession: SessionBackingOff,
      listeners: SessionNoConnectionListeners,
    ): SessionNoConnection => {
      const carriedState = inheritSharedSessionWithGrace(oldSession);
      oldSession._handleStateExit();

      const session = new SessionNoConnection({
        listeners,
        ...carriedState,
      });
      session.log?.info(
        `session ${session.id} transition from BackingOff to NoConnection`,
        {
          ...session.loggingMetadata,
          tags: ['state-transition'],
        },
      );

      return session;
    },
    ConnectingToNoConnection: <ConnType extends Connection>(
      oldSession: SessionConnecting<ConnType>,
      listeners: SessionNoConnectionListeners,
    ): SessionNoConnection => {
      const carriedState = inheritSharedSessionWithGrace(oldSession);
      oldSession.bestEffortClose();
      oldSession._handleStateExit();

      const session = new SessionNoConnection({
        listeners,
        ...carriedState,
      });
      session.log?.info(
        `session ${session.id} transition from Connecting to NoConnection`,
        {
          ...session.loggingMetadata,
          tags: ['state-transition'],
        },
      );

      return session;
    },
    HandshakingToNoConnection: <ConnType extends Connection>(
      oldSession: SessionHandshaking<ConnType>,
      listeners: SessionNoConnectionListeners,
    ): SessionNoConnection => {
      const carriedState = inheritSharedSessionWithGrace(oldSession);
      oldSession.conn.close();
      oldSession._handleStateExit();

      const session = new SessionNoConnection({
        listeners,
        ...carriedState,
      });
      session.log?.info(
        `session ${session.id} transition from Handshaking to NoConnection`,
        {
          ...session.loggingMetadata,
          tags: ['state-transition'],
        },
      );

      return session;
    },
    ConnectedToNoConnection: <ConnType extends Connection>(
      oldSession: SessionConnected<ConnType>,
      listeners: SessionNoConnectionListeners,
    ): SessionNoConnection => {
      const carriedState = inheritSharedSession(oldSession);
      const graceExpiryTime =
        Date.now() + oldSession.options.sessionDisconnectGraceMs;
      oldSession.conn.close();
      oldSession._handleStateExit();

      const session = new SessionNoConnection({
        listeners,
        graceExpiryTime,
        ...carriedState,
      });
      session.log?.info(
        `session ${session.id} transition from Connected to NoConnection`,
        {
          ...session.loggingMetadata,
          tags: ['state-transition'],
        },
      );

      return session;
    },
  },
} as const;

const transitions = SessionStateGraph.transition;

export const ClientSessionStateGraph = {
  entrypoint: SessionStateGraph.entrypoints.NoConnection,
  transition: {
    // happy paths
    // NoConnection -> BackingOff: attempt to connect
    NoConnectionToBackingOff: transitions.NoConnectionToBackingOff,
    // BackingOff -> Connecting: backoff period elapsed, start connection
    BackingOffToConnecting: transitions.BackingOffToConnecting,
    // Connecting -> Handshaking: connection established, start handshake
    ConnectingToHandshaking: transitions.ConnectingToHandshaking,
    // Handshaking -> Connected: handshake complete, session ready
    HandshakingToConnected: transitions.HandshakingToConnected,

    // disconnect paths
    // BackingOff -> NoConnection: unused
    BackingOffToNoConnection: transitions.BackingOffToNoConnection,
    // Connecting -> NoConnection: connection failed or connection timeout
    ConnectingToNoConnection: transitions.ConnectingToNoConnection,
    // Handshaking -> NoConnection: connection closed or handshake timeout
    HandshakingToNoConnection: transitions.HandshakingToNoConnection,
    // Connected -> NoConnection: connection closed
    ConnectedToNoConnection: transitions.ConnectedToNoConnection,

    // destroy/close paths
    // NoConnection -> x: grace period elapsed
    // BackingOff -> x: grace period elapsed
    // Connecting -> x: grace period elapsed
    // Handshaking -> x: grace period elapsed or invalid handshake message or handshake rejection
    // Connected -> x: grace period elapsed or invalid message
  },
};

export type ClientSession<ConnType extends Connection> =
  | SessionNoConnection
  | SessionBackingOff
  | SessionConnecting<ConnType>
  | SessionHandshaking<ConnType>
  | SessionConnected<ConnType>;

export const ServerSessionStateGraph = {
  entrypoint: SessionStateGraph.entrypoints.WaitingForHandshake,
  transition: {
    // happy paths
    // WaitingForHandshake -> Connected: handshake complete, session ready
    WaitingForHandshakeToConnected: transitions.WaitingForHandshakeToConnected,

    // disconnect paths
    // Connected -> NoConnection: connection closed
    ConnectedToNoConnection: transitions.ConnectedToNoConnection,

    // destroy/close paths
    // WaitingForHandshake -> x: handshake timeout elapsed or invalid handshake message or handshake rejection or connection closed
  },
};

export type ServerSession<ConnType extends Connection> =
  // SessionWaitingForHandshake<ConnType> is stored separately in the server transport
  SessionConnected<ConnType> | SessionNoConnection;

export type Session<ConnType extends Connection> =
  | ClientSession<ConnType>
  | ServerSession<ConnType>;
